/** @jsxImportSource theme-ui @jsxRuntime classic */
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { a, useTransition, config } from '@react-spring/web';
import React, { useState, CSSProperties } from 'react';
import * as Icons from '@tishman/icons';

import {
  Grid,
  Box,
  Text,
  Link,
  DetailImageCardCarousel,
  DetailImageCarouselCardProps,
  usePagination,
  useFadeAnimationTrail,
  CarouselV2ContextProvider,
  Button,
} from '..';

import { OnlyHereHeading } from './OnlyHereHeading';

interface OnlyHereProps {
  caption: string;
  cards: DetailImageCarouselCardProps[];
}

export const OnlyHere = ({
  caption,
  cards,
}: OnlyHereProps): React.JSX.Element => {
  const pageState = usePagination({ pages: cards.length });
  const nextImage = pageState.hasNext ? pageState.page + 1 : null;
  const [toggle, setToggle] = useState(true);

  const [trails, trailRef] = useFadeAnimationTrail({
    numberOfItems: 3,
    threshold: 0.4,
  });

  const transition = useTransition(cards[pageState.page], {
    from: { opacity: 0, y: toggle ? 170 : -170 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: toggle ? -170 : 170 },
  });

  const fade = useTransition(cards[pageState.page], {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const fadeDelay = useTransition(
    cards[pageState.hasNext ? pageState.page + 1 : pageState.page],
    {
      config: config.slow,
      from: { opacity: 0 },
      enter: { opacity: 1 },
      leave: { opacity: 0 },
    },
  );

  return (
    <Box
      data-module="OnlyHere"
      sx={{ pb: [0, null, 4, 3, 4], pt: [8, '175px', '175px', 0, 0] }}
    >
      <OnlyHereHeading
        caption={caption}
        link={{
          label: 'Explore All',
          url: '/attractions/',
          description: 'attractions',
        }}
        title="Only Here"
      />
      <CarouselV2ContextProvider>
        <Box aria-label="Rockefeller Center Attractions Carousel" role="region">
          <Grid
            aria-label={`slide ${pageState?.page + 1} of ${pageState?.pages}`}
            ref={trailRef}
            role="group"
            sx={{
              height: 507,
              gridTemplateColumns: [
                null,
                null,
                null,
                '665px 300px 1fr',
                '708px 407px 1fr',
              ],
              gridTemplateRows: 507,
              gap: 0,
              display: ['none', 'none', 'none', 'grid', 'grid'],
            }}
          >
            {/* FIRST IMAGE ON THE LEFT */}
            <a.div style={{ ...trails[0], position: 'relative' }}>
              {fade((firstImgStyle, firstImg) => (
                <a.div style={firstImgStyle}>
                  {getImage(firstImg.gatsbyImageData) && (
                    <GatsbyImage
                      alt={firstImg.alt}
                      image={firstImg.gatsbyImageData!}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                      }}
                    />
                  )}
                </a.div>
              ))}
            </a.div>
            {/* TEXT AND ARROWS IN THE MIDDLE */}
            <a.div
              style={{
                ...trails[1],
                paddingLeft: '32px',
                position: 'relative',
                flexDirection: 'column',
                justifyContent: 'center',
                display: 'flex',
                maxWidth: 290,
              }}
            >
              {transition((textStyle, item) => (
                <a.div style={{ position: 'absolute', ...textStyle }}>
                  {item.title === 'The Rink at Rockefeller Center' ? (
                    <Box sx={{ maxWidth: 244, mb: 2 }}>
                      <Icons.RinkLogo
                        style={{
                          color: 'black',
                          maxWidth: 230,
                          minWidth: 215,
                          width: '100%',
                          height: 'auto',
                        }}
                      />
                    </Box>
                  ) : (
                    <Text
                      as="h3"
                      sx={{
                        variant: 'text.mediumTitle',
                        letterSpacing: 3,
                        maxWidth: 244,
                        mb: 2,
                      }}
                    >
                      {item.title}
                    </Text>
                  )}
                  <Text sx={{ mb: 3 }} variant="mediumP">
                    {item.description}
                  </Text>
                  <Link
                    href={item.link?.url || ''}
                    sx={{ width: 'fit-content' }}
                    variant="underline"
                  >
                    {item.link?.label || ''}
                    <span className="sr-only">{` about ${item?.title}`}</span>
                  </Link>
                </a.div>
              ))}
              <NavArrows
                containerStyle={{
                  position: 'absolute',
                  bottom: '48px',
                  left: '16px',
                }}
                isBeginning={!pageState.hasPrevious}
                isEnd={!pageState.hasNext}
                onNextClick={() => {
                  pageState.hasNext && pageState.goto.next();
                  setToggle(true);
                }}
                onPrevClick={() => {
                  pageState.hasPrevious && pageState.goto.previous();
                  setToggle(false);
                }}
              />
            </a.div>

            {/* SECOND IMAGE ON THE RIGHT */}
            <a.div style={{ ...trails[2], position: 'relative' }}>
              {fadeDelay((secondImgStyle, secondImg) => (
                <a.div style={secondImgStyle}>
                  {nextImage && (
                    <GatsbyImage
                      alt={secondImg.alt}
                      image={secondImg?.gatsbyImageData}
                      imgStyle={{ objectPosition: 'left' }}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                      }}
                    />
                  )}
                </a.div>
              ))}
            </a.div>
          </Grid>
        </Box>
      </CarouselV2ContextProvider>
      <DetailImageCardCarousel
        data={cards}
        sx={{ display: ['flex', 'flex', 'flex', 'none', 'none'] }}
        title=" "
      />
    </Box>
  );
};

interface NavArrowsProps {
  containerStyle?: CSSProperties;
  onNextClick?: () => void;
  onPrevClick?: () => void;
  isBeginning?: boolean;
  isEnd?: boolean;
}

export const NavArrows = ({
  containerStyle,
  onNextClick,
  onPrevClick,
  isBeginning = false,
  isEnd = false,
  ...props
}: NavArrowsProps) => {
  return (
    <Box data-testid="carousel-navigation" style={containerStyle} {...props}>
      <Button
        data-element="CarouselNavigationPrevious"
        disabled={isBeginning}
        onClick={() => {
          onPrevClick?.();
        }}
        sx={{
          'cursor': 'pointer',
          ':not(:disabled):hover svg': {
            transform: `translateX(-5px) scale(${24 / 18}) rotate(180deg)`,
          },
        }}
        tabIndex={0}
        variant="icon"
      >
        <Icons.Arrow
          aria-hidden
          style={{
            transition: 'transform 0.3s ease-in-out',
            transform: `scale(${24 / 18}) rotate(180deg)`,
          }}
        />
      </Button>
      <Button
        data-element="CarouselNavigationNext"
        disabled={isEnd}
        onClick={() => {
          onNextClick?.();
        }}
        sx={{
          'cursor': 'pointer',
          ':not(:disabled):hover svg': {
            transform: `translateX(5px) scale(${24 / 18}) `,
          },
        }}
        tabIndex={0}
        variant="icon"
      >
        <Icons.Arrow
          aria-hidden
          style={{
            transition: 'transform 0.3s ease-in-out',
            transform: `scale(${24 / 18})`,
          }}
        />
      </Button>
    </Box>
  );
};
